import React, { useState, useEffect, useRef, useId, useMemo } from 'react';
import ReactApexChart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { Box, Typography } from '@mui/material';
import SquareIcon from '@mui/icons-material/Square';
import { FONT_COLOR } from '../const/general'
import { SHELTER_ANALYSIS_BAR_LABEL,SHELTER_ANALYSIS_BAR_COLOR } from '../const/color';
import VerticalSlider from '../item/slider_vertical'
import Progress from '../item/progress'

export default function VerticalBarShelterAnalysis(props) {
    const [sliderValue, setSliderValue] = useState([0, props.distance]);
    const [categories, setCategories] = useState([]);
    const [allData, setAllData] = useState([]);
    const [riskData, setRiskData] = useState([]); // 通常は series 配列（複数 series の場合もあり）
    const [seriesMax, setSeriesMax] = useState(props.limitVal + 1);
    const [isSlider, setIsSlider] = useState(false);
    const [dataLength, setDataLength] = useState(0);
    const [isNoData, setIsNoData] = useState(false);
    const [areaNameAry, setAreaNameAry] = useState([]);
    const [areaRiverNameAry, setAreaRiverNameAry] = useState([]);
    const [tooltipInfo, setTooltipInfo] = useState(null);

    // stacked 判定と色配列
    const [stackedMode, setStackedMode] = useState(false);
    const [stackedColors, setStackedColors] = useState([]);

    // チャートごとの一意のキー
    const chartId = useId();
    // 監視用Ref
    const sliderValueRef = useRef(sliderValue);

    // 監視用Ref（ホイール）
    const containerRef = useRef(null);
    const wheelHandlerRef = useRef(null);
    const wheelTimeoutRef = useRef(null);

    const handleWheel = (e) => {
        if (!isSlider || dataLength <= props.distance) return;
        e.preventDefault();
        if (wheelTimeoutRef.current) return;

        const delta = e.deltaY;
        const stepSize = Math.max(1, Math.round(Math.abs(delta) / 50)); // 感度: 50 を小さくすると更に速くなる
        setSliderValue(prev => {
            const maxStart = Math.max(0, dataLength - props.distance);
            let start = prev[0] + (delta > 0 ? -stepSize : stepSize);
            if (start < 0) start = 0;
            if (start > maxStart) start = maxStart;
            return [start, start + props.distance];
        });

        wheelTimeoutRef.current = setTimeout(() => {
            wheelTimeoutRef.current = null;
        }, 10);
    };

    const getLabelValue = (val) => {
        if (val === '-') return '-';
        const num = Number(val);
        if (!isNaN(num)) return String(Math.round(num)); // 小数点なしにする
        return '';
    };

    const getYValue = (val) => {
        if (val === '-') return 0;
        const num = Number(val);
        return !isNaN(num) ? num : 0;
    };

    // 被害キー順／ラベル／色（必要なら順序を調整）
    const SHELTER_ANALYSIS_KEYS = [
        ['rate_group_a_hemolytic', SHELTER_ANALYSIS_BAR_LABEL.GROUP_A_HEMOLYTIC, SHELTER_ANALYSIS_BAR_COLOR.GROUP_A_HEMOLYTIC],
        ['rate_bacterial_meningitis', SHELTER_ANALYSIS_BAR_LABEL.BACTERIAL_MENINGITIS, SHELTER_ANALYSIS_BAR_COLOR.BACTERIAL_MENINGITIS],
        ['rate_exanthem_subitum', SHELTER_ANALYSIS_BAR_LABEL.EXANTHEM_SUBITUM, SHELTER_ANALYSIS_BAR_COLOR.EXANTHEM_SUBITUM],
        ['rate_aseptic_spinal_cord_meningitis', SHELTER_ANALYSIS_BAR_LABEL.ASEPTIC_SPINAL_CORD_MENINGITIS, SHELTER_ANALYSIS_BAR_COLOR.ASEPTIC_SPINAL_CORD_MENINGITIS],
        ['rate_epidemic_conjunctivitis', SHELTER_ANALYSIS_BAR_LABEL.EPIDEMIC_CONJUNCTIVITIS, SHELTER_ANALYSIS_BAR_COLOR.EPIDEMIC_CONJUNCTIVITIS],
        ['rate_chicken_pox', SHELTER_ANALYSIS_BAR_LABEL.CHICKEN_POX, SHELTER_ANALYSIS_BAR_COLOR.CHICKEN_POX],
        ['rate_acute_infectious_gastroenteritis', SHELTER_ANALYSIS_BAR_LABEL.ACUTE_INFECTIOUS_GASTROENTERITIS, SHELTER_ANALYSIS_BAR_COLOR.ACUTE_INFECTIOUS_GASTROENTERITIS],
        ['rate_hand_foot_mouth', SHELTER_ANALYSIS_BAR_LABEL.HAND_FOOT_MOUTH, SHELTER_ANALYSIS_BAR_COLOR.HAND_FOOT_MOUTH],
        ['rate_acute_hemorrhagic_conjunctivitis', SHELTER_ANALYSIS_BAR_LABEL.ACUTE_HEMORRHAGIC_CONJUNCTIVITIS, SHELTER_ANALYSIS_BAR_COLOR.ACUTE_HEMORRHAGIC_CONJUNCTIVITIS],
        ['rate_apple', SHELTER_ANALYSIS_BAR_LABEL.APPLE, SHELTER_ANALYSIS_BAR_COLOR.APPLE],
        ['rate_pharyngoconjunctival', SHELTER_ANALYSIS_BAR_LABEL.PHARYNGOCONJUNCTIVAL, SHELTER_ANALYSIS_BAR_COLOR.PHARYNGOCONJUNCTIVAL],
        ['rate_rota', SHELTER_ANALYSIS_BAR_LABEL.ROTA, SHELTER_ANALYSIS_BAR_COLOR.ROTA],
        ['rate_mumps', SHELTER_ANALYSIS_BAR_LABEL.MUMPS, SHELTER_ANALYSIS_BAR_COLOR.MUMPS],
        ['rate_herpangina', SHELTER_ANALYSIS_BAR_LABEL.HERPANGINA, SHELTER_ANALYSIS_BAR_COLOR.HERPANGINA],
        ['rate_chlamydia', SHELTER_ANALYSIS_BAR_LABEL.CHLAMYDIA, SHELTER_ANALYSIS_BAR_COLOR.CHLAMYDIA],
        ['rate_rs', SHELTER_ANALYSIS_BAR_LABEL.RS, SHELTER_ANALYSIS_BAR_COLOR.RS],
        ['rate_myco_child', SHELTER_ANALYSIS_BAR_LABEL.MYCO_CHILD, SHELTER_ANALYSIS_BAR_COLOR.MYCO_CHILD],
        ['rate_myco', SHELTER_ANALYSIS_BAR_LABEL.MYCO, SHELTER_ANALYSIS_BAR_COLOR.MYCO],
        ['rate_flu', SHELTER_ANALYSIS_BAR_LABEL.FLU, SHELTER_ANALYSIS_BAR_COLOR.FLU],
        ['rate_covid', SHELTER_ANALYSIS_BAR_LABEL.COVID_19, SHELTER_ANALYSIS_BAR_COLOR.COVID_19]
    ];

    // チャート情報を格納
    const setChartData = (data) => {
        const isOver = props.distance < data.length;
        const { rankData, series_ary, category_ary, tempAreaNameAry, tempAreaRiverNameAry } = props.getRankData(data, isOver, sliderValueRef, getLabelValue, getYValue);
        // rankData は市町村オブジェクト配列（例: { city_code, city_name, total, int_* }）
        // 被害種別フィールドがあるか判定（小文字・大文字両対応）
        const sample = rankData && rankData.length > 0 ? rankData[0] : null;
        const hasDamageFields = sample && SHELTER_ANALYSIS_KEYS.some(k => (k[0] in sample) || (k[0].toUpperCase() in sample));

        if (hasDamageFields) {
            // 積み上げシリーズを作る
            const cats = rankData.map(item => item.area || item.name || (item.cityName) || '');

            const builtSeries = [];
            const builtColors = [];

            SHELTER_ANALYSIS_KEYS.forEach(([key, label, color]) => {
                const keyLower = key;
                const keyUpper = key.toUpperCase();
                const seriesData = rankData.map(item => {
                    const rawVal = (item[keyLower] !== undefined) ? item[keyLower] : (item[keyUpper] !== undefined ? item[keyUpper] : 0);
                    const y = Number(rawVal) || 0;
                    return {
                        x: '',
                        y,
                        custom: {
                            code: item.code || '',
                            area: item.area || '',
                            total: item.total || 0
                        }
                    };
                })
                const totalForSeries = seriesData.reduce((s, p) => s + (Number(p.y) || 0), 0);
                if (totalForSeries > 0) {
                    builtSeries.push({
                        name: label,
                        data: seriesData
                    });
                    builtColors.push(color);
                }
            });

            // categories と series を上書き
            setAllData(rankData);
            setRiskData(builtSeries);
            setCategories(cats);
            setIsSlider(isOver);
            setDataLength(rankData.length);
            setAreaNameAry(tempAreaNameAry);
            setAreaRiverNameAry(tempAreaRiverNameAry);

            // stacked フラグ / 色をセット
            setStackedMode(true);
            setStackedColors(builtColors);

            // seriesMax を更新（各カテゴリの合計最大を用いる）
            const sums = rankData.map(item => {
                return SHELTER_ANALYSIS_KEYS.reduce((acc, [key]) => {
                    const v = (item[key] !== undefined) ? Number(item[key]) : (item[key.toUpperCase()] !== undefined ? Number(item[key.toUpperCase()]) : 0);
                    return acc + (isNaN(v) ? 0 : v);
                }, 0);
            });
            const maxSum = sums.length > 0 ? Math.max(...sums) : props.limitVal;
            setSeriesMax(Math.max(props.limitVal + props.plusValue, maxSum + props.plusValue));
            return;
        }

        // 被害フィールドがない場合は既存ロジック（単一 series）に従う
        setAllData(rankData);
        setRiskData(series_ary);
        setCategories(category_ary);
        setIsSlider(isOver);
        setDataLength(rankData.length);
        setAreaNameAry(tempAreaNameAry);
        setAreaRiverNameAry(tempAreaRiverNameAry);
        setStackedMode(false);
        setStackedColors([]);

        setSeriesMax(props.limitVal + props.plusValue)
        if (rankData.length > 0) {
            // 既存の最大値の取得
            const max = Math.max(...rankData.map(item => item.max || item.total || 0));
            if (max > props.limitVal) {
                setSeriesMax(max + props.plusValue)
            }
        }
    }

    // ローディング終了
    const loadingEnd = () => {
        props.setReady(prev => {
            const updated = { ...prev };
            props.isReadyKey.forEach(key => {
                if (key in updated) {
                    updated[key] = true;
                }
            });
            return updated;
        });
    }

    // series の形状は stackedMode によって変わる
    const series = useMemo(() => {
        if (stackedMode) {
            return riskData; // すでに複数 series 配列
        }
        return [
            {
                name: '',
                data: riskData
            },
        ];
    }, [riskData, stackedMode]);

    // データ更新が完了したらローディング終了
    useEffect(() => {
        if (allData !== null) {
            const timer = setTimeout(() => {
                loadingEnd();
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [allData]);

    // 定期監視用
    useEffect(() => {
        if (props.allRankData === null ||
            props.allRankData === undefined ||
            props.allRankData.length === 0
        ) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.allRankData && props.allRankData.length > 0) {
            setChartData(props.allRankData)
        }
        props?.setSelectedRoadName?.(null);
        setIsNoData(false)

        // 依存関係の「dateArray」は削除しないでください。
        // これがないとprops.allRankDataに変化がない場合にローディングが消えなくなるので
    }, [props.dateArray, props.allRankData, props.setSelectedRoadName]);

    useEffect(() => {
        if (props.allRankData === null ||
            props.allRankData === undefined ||
            props.allRankData.length === 0
        ) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.allRankData && props.allRankData.length > 0) {
            setChartData(props.allRankData)
        }
        props?.setSelectedRoadName?.(null);
        setIsNoData(false)

        return () => {
            ApexCharts.exec(chartId, 'destroy'); // 明示的に破棄
        };
    }, []);

    // スライダーが移動された場合
    useEffect(() => {
        sliderValueRef.current = sliderValue;
        setChartData(allData)
    }, [sliderValue]);

    useEffect(() => {
        wheelHandlerRef.current = handleWheel;
    }, [handleWheel]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const fn = (e) => {
            if (wheelHandlerRef.current) wheelHandlerRef.current(e);
        };
        el.addEventListener('wheel', fn, { passive: false });
        return () => el.removeEventListener('wheel', fn);
    }, []);

    const options = useMemo(() => ({
        chart: {
            id: chartId,
            type: "bar",
            toolbar: {
                show: false,
            },
            offsetX: -30,
            offsetY: 0,
            stacked: stackedMode,
            events: {
                dataPointSelection: (event, chartContext, config) => {
                    // ローディング開始
                    return;
                    props.setReady(prev => {
                        const updated = Object.fromEntries(
                            Object.keys(prev).map(key => [key, false])
                        );
                        return updated;
                    });
                    const seriesIndex = config.seriesIndex;
                    const dataPointIndex = config.dataPointIndex;
                    if (seriesIndex === -1 || dataPointIndex === -1) return; // クリック対象がない場合ガード
                    const clickedItem = series[seriesIndex].data[dataPointIndex];
                },
                dataPointMouseEnter: (event, chartContext, config) => {
                    if (props?.dataPointMouseEnter) {
                        setTooltipInfo(props.dataPointMouseEnter(event, config, series));
                    }
                },
                mouseLeave: () => {
                    setTooltipInfo(null);
                }
            }
        },
        annotations: props.annotations,
        legend: { show: false },
        colors: [
            function ({ value, seriesIndex, dataPointIndex, w }) {
                if (props.getRankColor) {
                    return props.getRankColor(value, series, seriesIndex, dataPointIndex, w)
                }
            }
        ],
        plotOptions: {
            bar: {
                horizontal: true,
                stacked: stackedMode,
                dataLabels: {
                    position: 'center', // 縦方向中央に配置
                }
            }
        },
        grid: {
            ...props.grid
        },
        dataLabels: {
            enabled: true,
            style: {
                fontSize: props.fontSize,
                colors: [
                    function ({ seriesIndex, dataPointIndex, w }) {
                        const dataPoint = w.config.series[seriesIndex].data[dataPointIndex];
                        // 水平バーの場合、dataPointは { x: '', y: value, custom: {...} } の形式
                        const actualValue = dataPoint.y;
                        if (props.getRankLabelColor) {
                            return props.getRankLabelColor(actualValue, series, seriesIndex, dataPointIndex, w)
                        }
                        // デフォルトの色を返す
                        return FONT_COLOR;
                    }
                ]
            },
            formatter: function (val, opts) {
                return props.getFormat ? props.getFormat(val, opts) : val.toString();
            }
        },
        xaxis: {
            categories: categories,
            axisTicks: {
                show: false
            },
            axisBorder: {
                show: false
            },
            labels: {
                show: false
            },
            min: 0,
            max: seriesMax
        },
        yaxis: {
            axisTicks: {
                show: false
            },
            axisBorder: {
                show: false
            },
            labels: {
                show: true,
                minWidth: 0,
                style: {
                    colors: FONT_COLOR,
                    fontSize: '12px'
                },
                offsetX: 0,
                offsetY: 0,
                rotate: 0,
                formatter: (value) => {
                    return props.getLabelFormat(value)
                }
            }
        },
        tooltip: {
            enabled: props.enabled,
            x: {
                formatter: function (val, opts) {
                    return props.tooltipFormatX(opts);
                }
            },
            y: {
                formatter: function (val, opts) {
                    return props.tooltipFormatY(val);
                }
            },
            custom: function (opts) {
                return props.tooltipFormatCustom(opts, areaNameAry, areaRiverNameAry);
            }
        },
    }), [categories, seriesMax, stackedMode, JSON.stringify(stackedColors)]);

    // チャートのデータが更新されたら強制再描画
    useEffect(() => {
        if (series && series.length > 0) {
            ApexCharts.exec(chartId,
                "updateSeries", series);
        }
    }, [series]);

    return (
        <>
            <Box
                ref={containerRef}
                sx={{
                    display: 'flex',
                    height: props.height,
                    overflow: 'hidden', // スクロール非表示
                }}
            >
                {/* チャート部分 */}
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    {!props.isReadyFlg ?
                        <Progress />
                        :
                        isNoData ?
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold',
                                    color: 'white'
                                }}
                            >
                                対象データがありません
                            </Typography>
                            :
                            <Box sx={{ display: 'flex', position: 'relative', flex: 1, overflow: 'hidden', height: "100%" }}>

                                {/* チャート本体 */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <ReactApexChart
                                        options={options}
                                        series={series}
                                        type="bar"
                                        height="70%"
                                        width="100%"
                                        style={{ visibility: props.isReadyFlg ? 'visible' : 'hidden' }}
                                    />
                                    {tooltipInfo?.data && (
                                        props.tooltipContent(tooltipInfo)
                                    )}
                                </Box>
                            </Box>
                    }
                </Box>
                {/* スライダー部分 */}
                {isSlider &&
                    <Box
                        sx={{
                            width: '20px', // スライダー幅
                            marginLeft: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <VerticalSlider
                            dataLength={dataLength}
                            value={sliderValue}
                            setValue={setSliderValue}
                            distance={props.distance}
                            height={'100%'}
                        />
                    </Box>
                }
            </Box>
        </>
    );
}